/* The audit chain: who did what, to which subject, when.
 *
 * **Rebuilt from the Claude Design artboard, which is the source of truth for this screen.** The
 * reference is one header, one notice band, one four-column table and one pager. What it replaced
 * carried a filter card with three inputs and two buttons, a six-column table, a caption, a
 * truncation footnote and an entry count in the pager — none of which the artboard has. Every one
 * of those is gone rather than restyled.
 *
 * **The filter card went from this screen, not from the platform.** `GET /v1/audit/entries` still
 * takes `actor`, `action`, `aggregate_type`, `aggregate_id`, `occurred_from` and `occurred_to`, and
 * `AuditFilters` still serves the Marketplace's activity tab, the connection detail panel and the
 * front page's feed. What went is the three-input card the reference does not draw.
 *
 * Two controls have since been added above the table — a period filter and a search over the actor
 * column — and neither is a return of that card. The reference draws no filter at all, so there is
 * no reference paint to be unfaithful to; what it does draw is a toolbar's worth of room above the
 * table, and both controls answer a question a reader of a 50-row page of a seven-year chain
 * actually has.
 *
 * Four things this screen is deliberate about, and three of them are the reference's own decisions.
 *
 * **It says the snapshot is absent, in the artboard's words.** The route returns no `payload` — it is
 * not selected by the statement, so no snapshot leaves PostgreSQL — and every response carries
 * `meta.payload_omitted`. A table of actions with no detail column looks like a table whose entries
 * happen to have no detail, so the omission is stated. The band's copy is the reference's rather
 * than ours, down to the em dash.
 *
 * **Hashes are shown, thirteen characters, with the full value on hover.** A digest discloses
 * nothing and an auditor spot-checking a chain against a verification result needs it.
 *
 * **A `system:` actor is rendered differently from a person** — mono and muted against the body face
 * at 500. The platform writes those deliberately for unattended sweeps, and "who did this" has a
 * different answer and a different follow-up for a timer than for a human.
 *
 * **There is no verify button, and the reference has none either.** `POST /verify-chain` exists,
 * `lumiq-cli` exposes it and a schedule submits one every 24 hours; a button beside a listing
 * invites one submission per curiosity, and `verify:audit.chain` is an admin grant. This screen
 * shows what the nightly walk recorded.
 *
 * Paging is the platform's own signed cursor. There is a page number and never a total, because a
 * keyset listing cannot know how many pages exist without counting the whole table.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useCursorStack } from '@/shared/api/paging';
import type { AuditEntry } from '@/shared/api/generated/audit';
import { TextField } from '@/shared/ui/Field';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import type { SegmentOption } from '@/shared/ui/SegmentedFilter';

import { useAuditEntries } from '../api/audit';
import {
  absoluteInstant,
  actionLabel,
  entryKey,
  isSystemActor,
  RANGE_IDS,
  shortHash,
  whenLabel,
  windowFor,
} from '../model/presentation';
import type { RangeId } from '../model/presentation';

export function AuditTrailScreen(): ReactElement {
  const { t } = useTranslation();
  /* The cursor stack, from `@/shared/api/paging`. Back needs a stack because keyset paging is
   * forward-only by construction: a cursor names where to resume, not where to return to. */
  const pager = useCursorStack();
  const [range, setRange] = useState<RangeId>('all');
  /* The raw box contents. `actor.trim()` is what reaches the request, so a stray space cannot
     become a filter that matches nothing — the server's match is a substring and a leading space is
     part of it. */
  const [actor, setActor] = useState('');

  /* Read once per render rather than per row, so every Time cell on a page agrees about what
   * "Today" means. A component that read the clock per cell would answer differently across a
   * midnight boundary mid-table. */
  const now = new Date();

  /* Memoised on the range rather than recomputed per render, because the window is part of the
   * request's cache key: a fresh `occurredTo` on every render would be a new key every render and
   * the screen would refetch itself in a loop. */
  const searching = actor.trim() !== '';
  /* **Spread in only when there is something to search for.** `AuditFilters.actor` is
     `string | undefined` under `exactOptionalPropertyTypes`, and the distinction is load-bearing at
     the wire: an `actor=` sent empty is a literal filter value the server matches nothing against,
     so a cleared box would return a page reading as "this tenant did nothing" rather than the
     unfiltered chain. */
  const filters = useMemo(
    () => ({
      ...windowFor(range, new Date()),
      ...(actor.trim() === '' ? {} : { actor: actor.trim() }),
    }),
    [range, actor],
  );
  const page = useAuditEntries(filters, pager.cursor);
  const entries = page.data?.items ?? [];
  const filtered = range !== 'all';

  const options: readonly SegmentOption<RangeId>[] = RANGE_IDS.map((id) => ({
    id,
    label: t(`audit.range.${id}`),
  }));

  return (
    <section className="lq-aud">
      <div className="lq-aud__head">
        <div className="lq-aud__headMain">
          <h1 className="lq-aud__h1">{t('audit.heading')}</h1>
          <p className="lq-aud__lede">{t('audit.lede')}</p>
        </div>
      </div>

      <div className="lq-aud__band">
        <strong className="lq-aud__bandLabel">{t('audit.snapshotOmitted.title')}</strong>
        <span className="lq-aud__bandBody">{t('audit.snapshotOmitted.body')}</span>
      </div>

      {/* Above the table it controls, which is where this design system puts a toolbar.
          `SegmentedFilter`'s `ledger` variant rather than a new audit-local chip row: it is already
          square, already bordered in `--border-strong`, and already carries `role="group"` with
          `aria-pressed` — and a second answer to "a row of mutually-exclusive filters" is how two
          screens come to disagree about what a pressed one looks like. The artboard draws no filter
          at all, so there is no reference paint to be unfaithful to. */}
      <div className="lq-aud__toolbar">
        {/* **Over the actor column rather than over everything.** The chain's other columns are
            either a closed vocabulary the platform emits (the action) or a digest (the hash), and
            neither is something somebody types half of. An actor is a person's address or a
            `system:` sweep name, which is exactly what an auditor arrives holding.

            The server matches a substring, so typing part of a name works — `%` and `_` are matched
            literally, bound as a parameter with `ESCAPE`, so a value cannot widen its own match. */}
        <div className="lq-aud__search">
          <TextField
            label={t('audit.search.label')}
            type="search"
            inline
            value={actor}
            placeholder={t('audit.search.placeholder')}
            onChange={(next) => {
              /* Same reason the range filter resets: a cursor is signed over a fingerprint of the
                 query it was issued for, so one carried across a search change is refused with a
                 400. */
              setActor(next);
              pager.reset();
            }}
          />
        </div>
        <SegmentedFilter
          options={options}
          value={range}
          variant="ledger"
          label={t('audit.range.label')}
          onChange={(id) => {
            /* The cursor is signed over the tenant *and* a fingerprint of the query, so carrying
               one across a range change is refused with a 400. Resetting says the same thing
               without the round trip, and it is the honest answer to a question that became a
               different question. */
            setRange(id);
            pager.reset();
          }}
        />
      </div>

      <div className="lq-aud__wrap">
        <table className="lq-aud__table">
          <thead>
            <tr>
              <th scope="col">{t('audit.column.when')}</th>
              <th scope="col">{t('audit.column.actor')}</th>
              <th scope="col">{t('audit.column.action')}</th>
              <th scope="col" className="lq-aud__right">
                {t('audit.column.hash')}
              </th>
            </tr>
          </thead>
          <tbody>
            {page.isPending ? (
              <tr>
                <td className="lq-aud__state" colSpan={4}>
                  {t('audit.loading')}
                </td>
              </tr>
            ) : null}

            {page.isError ? (
              <tr>
                <td className="lq-aud__state lq-aud__state--error" colSpan={4}>
                  {page.error instanceof Error
                    ? page.error.message
                    : t('audit.error.unknown')}
                </td>
              </tr>
            ) : null}

            {/* Three empty states, because they are three different facts. An unfiltered empty
                chain means nothing has been recorded for this tenant; a period-filtered one means
                nothing happened then; a search that found nothing means nobody matching that name
                did anything. Collapsing any pair would tell an auditor their estate has no history
                when they had merely picked a quiet day or mistyped a name.

                The search is checked first because it is the narrower claim: with both a term and a
                period, "nobody matching this acted" is what the reader most likely got wrong, and
                the sentence names the period too so neither half is hidden. */}
            {!page.isPending && !page.isError && entries.length === 0 ? (
              <tr>
                <td className="lq-aud__state" colSpan={4}>
                  {searching
                    ? t('audit.empty.search', {
                        actor: actor.trim(),
                        period: t(`audit.range.${range}`),
                      })
                    : filtered
                      ? t('audit.empty.filtered', { period: t(`audit.range.${range}`) })
                      : t('audit.empty.unfiltered')}
                </td>
              </tr>
            ) : null}

            {entries.map((entry) => (
              <Row entry={entry} now={now} key={entryKey(entry)} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="lq-aud__pager">
        <span>{t('audit.pager.page', { page: pager.page })}</span>
        <span className="lq-aud__pagerSpacer" />
        <button
          type="button"
          className="lq-aud__pagerBtn"
          disabled={!pager.canGoBack}
          onClick={pager.back}
        >
          {t('audit.pager.back')}
        </button>
        <button
          type="button"
          className="lq-aud__pagerBtn"
          disabled={page.data?.nextCursor == null}
          onClick={() => {
            pager.forward(page.data?.nextCursor);
          }}
        >
          {t('audit.pager.next')}
        </button>
      </div>
    </section>
  );
}

function Row({
  entry,
  now,
}: {
  readonly entry: AuditEntry;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const system = isSystemActor(entry.actor);

  return (
    <tr>
      <td className="lq-aud__when" title={absoluteInstant(entry.occurred_at)}>
        {whenLabel(entry.occurred_at, now)}
      </td>
      {/* The class is on the cell for a person and on an inner span for a sweep, which is the
          artboard's own structure: the mono treatment is the actor's, not the cell's. */}
      {system ? (
        <td>
          <span className="lq-aud__system" title={t('audit.systemActor')}>
            {entry.actor}
          </span>
        </td>
      ) : (
        <td className="lq-aud__actor">{entry.actor}</td>
      )}
      {/* The aggregate id on the title rather than in the cell. A bare uuid beside a type name
          fills a column with something nobody can read, while an auditor tracing one subject does
          need it — and `title` is the device the reference already uses for the hash. */}
      <td className="lq-aud__action" title={entry.aggregate_id}>
        {actionLabel(entry)}
      </td>
      <td className="lq-aud__hash">
        <span title={entry.entry_hash}>
          <span className="lq-aud__digest">{shortHash(entry.entry_hash)}</span>
        </span>
      </td>
    </tr>
  );
}
